import { useState } from 'react'
import { useSupabaseQuery, useSupabaseMutation } from '../../hooks/useSupabase'
import Modal from '../shared/Modal'

export default function UrlsTab({ selectedAccount }) {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [deletingUrl, setDeletingUrl] = useState(null)
  const [createLoading, setCreateLoading] = useState(false)
  const [copiedId, setCopiedId] = useState(null)

  const { data: urls, loading, error, refetch } = useSupabaseQuery('short_urls',
    selectedAccount ? {
      eq: { is_deleted: false, account_id: selectedAccount.id },
      order: { column: 'created', ascending: false },
      limit: 100
    } : null
  )

  const { update, loading: mutating } = useSupabaseMutation('short_urls')

  const handleCreate = async (formData) => {
    try {
      setCreateLoading(true)
      const apiKey = prompt('Enter API key for this account:')
      if (!apiKey) return

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const res = await fetch(`${supabaseUrl}/functions/v1/Shorten-Url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          url: formData.url,
          ...(formData.slug && { slug: formData.slug }),
          ...(formData.ttl && { ttl: formData.ttl }),
          ...(formData.expiryUrl && { expiryUrl: formData.expiryUrl }),
        }),
      })

      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to create short URL')

      setShowCreateModal(false)
      refetch()
    } catch (err) {
      alert(err.message)
    } finally {
      setCreateLoading(false)
    }
  }

  const handleDelete = async () => {
    const { error } = await update(deletingUrl.id, { is_deleted: true }, 'id')
    if (!error) {
      setDeletingUrl(null)
      refetch()
    }
  }

  const handleCopy = async (text, urlId) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedId(urlId)
      setTimeout(() => setCopiedId(null), 2000)
    } catch {
      // Fallback for insecure contexts
      const textarea = document.createElement('textarea')
      textarea.value = text
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopiedId(urlId)
      setTimeout(() => setCopiedId(null), 2000)
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return null
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const truncate = (str, maxLen = 50) => {
    if (!str) return ''
    return str.length > maxLen ? str.slice(0, maxLen) + '...' : str
  }

  const getExpiryBadge = (expiryDatetime) => {
    if (!expiryDatetime) {
      return (
        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
          No expiry
        </span>
      )
    }

    const now = new Date()
    const expiry = new Date(expiryDatetime)

    if (expiry < now) {
      return (
        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200">
          Expired
        </span>
      )
    }

    return (
      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200">
        Active — {formatDate(expiryDatetime)}
      </span>
    )
  }

  if (!selectedAccount) {
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No account selected</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Please select an account from the Accounts tab first.
          </p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-800 dark:text-red-200">Failed to load URLs: {error.message}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Short URLs</h2>
          <p className="text-gray-600 dark:text-gray-400">
            URLs for: {selectedAccount.name}
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>New Short URL</span>
        </button>
      </div>

      {/* URLs Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Short URL
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Target
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Expiry
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {urls?.map((url) => (
              <tr key={url.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <code className="text-sm font-mono text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                    {url.shortened_url || url.slug}
                  </code>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <a
                    href={url.original_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    title={url.original_url}
                  >
                    {truncate(url.original_url)}
                  </a>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {formatDate(url.created)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getExpiryBadge(url.expiry_datetime_utc)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => handleCopy(url.shortened_url || url.slug, url.id)}
                    className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-3"
                    title="Copy short URL"
                  >
                    {copiedId === url.id ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={() => setDeletingUrl(url)}
                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {(!urls || urls.length === 0) && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No short URLs</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Create your first short URL to get started.
            </p>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <Modal title="Create Short URL" onClose={() => setShowCreateModal(false)}>
          <UrlForm
            onSubmit={handleCreate}
            onCancel={() => setShowCreateModal(false)}
            loading={createLoading}
          />
        </Modal>
      )}

      {/* Delete Confirmation */}
      {deletingUrl && (
        <Modal title="Delete Short URL" onClose={() => setDeletingUrl(null)} size="sm">
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">
              Are you sure you want to delete this short URL?
            </p>
            <code className="block text-sm font-mono bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 rounded">
              {deletingUrl.shortened_url || deletingUrl.slug}
            </code>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              This URL will stop redirecting immediately.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeletingUrl(null)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={mutating}
                className="px-4 py-2 text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {mutating ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

function UrlForm({ onSubmit, onCancel, loading }) {
  const [formData, setFormData] = useState({
    url: '',
    slug: '',
    ttl: '',
    expiryUrl: '',
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit(formData)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Target URL <span className="text-red-500">*</span>
        </label>
        <input
          type="url"
          required
          value={formData.url}
          onChange={(e) => setFormData({ ...formData, url: e.target.value })}
          placeholder="https://example.com/long-page-url"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Custom Slug
        </label>
        <input
          type="text"
          value={formData.slug}
          onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
          placeholder="custom-slug (optional)"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Leave blank to auto-generate a random slug.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          TTL (Time to Live)
        </label>
        <input
          type="text"
          value={formData.ttl}
          onChange={(e) => setFormData({ ...formData, ttl: e.target.value })}
          placeholder="e.g., 7d, 24h, 30m"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          How long the short URL stays active. Uses account default if omitted.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Expiry Redirect URL
        </label>
        <input
          type="url"
          value={formData.expiryUrl}
          onChange={(e) => setFormData({ ...formData, expiryUrl: e.target.value })}
          placeholder="https://example.com/expired"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Where to redirect after expiry. Falls back to account default.
        </p>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading || !formData.url}
          className="px-4 py-2 text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50"
        >
          {loading ? 'Creating...' : 'Create Short URL'}
        </button>
      </div>
    </form>
  )
}
